import type { PlanCalcInput, PlanDailyTargets, PlanMeta } from './diet-plan-templates';

// Versioned IDs keep previously recorded plans independent of the replacement formulas.
export const ATTACHED_FAT_LOSS_IDS = ['three-month-2026', 'sprint-40-2026'] as const;
export type AttachedFatLossId = typeof ATTACHED_FAT_LOSS_IDS[number];
export const isAttachedFatLoss = (id: string): id is AttachedFatLossId =>
  ATTACHED_FAT_LOSS_IDS.some(value => value === id);
export const ACTIVITY_OPTIONS = [
  { value: 1.2, label: '久坐，日常活动很少' },
  { value: 1.375, label: '轻度活动，每周运动 1–2 次' },
  { value: 1.55, label: '中等活动，每周运动 3–5 次' },
  { value: 1.725, label: '高活动量，每周运动 6–7 次' },
];
export const THREE_MONTH_TIERS = {
  male: [[2.2, 1.4, 0.8], [2.5, 1.6, 0.9], [3, 1.7, 1], [3.5, 1.8, 1.1]],
  female: [[2, 1.4, 1], [2.2, 1.6, 1.1], [2.5, 1.7, 1.1], [3, 1.8, 1.2]],
};
export function threeMonthTier(hours: number) {
  // The document has four whole-hour bands. Gaps use the lower band;
  // outside 2–9 h use the closest supplied band, never extrapolate.
  return hours >= 8 ? 3 : hours >= 6 ? 2 : hours >= 4 ? 1 : 0;
}
export const ATTACHED_FAT_LOSS_META: Record<AttachedFatLossId, PlanMeta> = {
  'three-month-2026': {
    id: 'three-month-2026', name: '三个月减脂计划', period: '90 天',
    tagline: '按性别和每周运动时长配餐，每 10 天复盘',
    coreLogic: '按体重 × 附件中的碳水、蛋白质、脂肪系数计算，不额外叠加高低碳倍率。',
    keyParams: ['每周 2–3 / 4–5 / 6–7 / 8–9 小时分档', '男女独立系数', '每 10 天核对体重与执行情况'],
    features: ['燕麦鸡蛋早餐、米饭午餐、薯类晚餐', '水果、坚果和烹调油全部计入营养', '不自动减少热量，不强制补剂'],
    isPremiumPlan: true,
  },
  'sprint-40-2026': {
    id: 'sprint-40-2026', name: '40 天冲刺减脂计划', period: '40 天',
    tagline: '按总消耗设置适度缺口，按周复盘',
    coreLogic: '使用实际年龄计算基础代谢，乘所选活动系数，再减 500 kcal；低于资料下限时缩小缺口。',
    keyParams: ['蛋白质 1.8 g/kg', '脂肪 0.9 g/kg', '剩余热量分配碳水'],
    features: ['前 7 天适应，之后执行并按周复盘', '连续两周停滞才考虑调整', '不设置高碳冲击日，不强制补剂'],
    isPremiumPlan: true,
  },
};

export function attachedPlanIssue(input: Pick<PlanCalcInput, 'weight' | 'height' | 'age' | 'weeklyHours' | 'template'>): string | null {
  if (!Number.isFinite(input.age) || (input.age || 0) < 18 || (input.age || 0) > 100)
    return '请先重新填写实际年龄；这两套减脂计划仅供成年人选择。';
  if (!Number.isFinite(input.weight) || input.weight <= 0 || !input.height || input.height <= 0)
    return '请先补全有效的身高和体重。';
  if (input.weight / (input.height / 100) ** 2 < 18.5)
    return '当前体重偏低，建议先评估营养状况，暂不启用减脂计划。';
  if (input.template === 'three-month-2026' && (!Number.isFinite(input.weeklyHours) || input.weeklyHours < 2 || input.weeklyHours > 9))
    return '三个月资料覆盖每周运动 2–9 小时；请核对测评数据，或选择 40 天计划。';
  return null;
}

export function calcAttachedFatLoss(input: PlanCalcInput): PlanDailyTargets {
  const weight = input.weight;
  let carb: number, protein: number, fat: number;
  if (input.template === 'three-month-2026') {
    const factors = THREE_MONTH_TIERS[input.gender][threeMonthTier(input.weeklyHours)];
    [carb, protein, fat] = factors.map(factor => Math.round(weight * factor));
  } else {
    const age = input.age ?? 25; // Legacy missing age is displayed as requiring a new assessment before activation.
    const bmr = 10 * weight + 6.25 * (input.height || 170) - 5 * age + (input.gender === 'male' ? 5 : -161);
    const activity = ACTIVITY_OPTIONS.some(option => option.value === input.activityFactor) ? input.activityFactor! : 1.55;
    const floor = input.gender === 'male' ? 1500 : 1200;
    protein = Math.round(weight * 1.8);
    fat = Math.round(weight * 0.9);
    // Do not generate zero/negative-carb prescriptions for unusually large profiles.
    // In that case the deficit is reduced, and the UI explains that review is needed.
    const calories = Math.max(floor, bmr * activity - 500, protein * 4 + fat * 9 + weight * 4);
    carb = Math.round((calories - protein * 4 - fat * 9) / 4);
  }
  return { calories: carb * 4 + protein * 4 + fat * 9, carb, protein, fat,
    salt: 5, waterMl: 2500, stageLabel: input.template === 'three-month-2026'
      ? '每 10 天复盘' : (input.dayIndex || 1) <= 7 ? '适应期' : '执行与每周复盘' };
}

export function attachedStage(id: AttachedFatLossId, day: number) {
  const end = id === 'three-month-2026' ? 90 : 40;
  return { stageLabel: day > end ? '本周期已完成' : id === 'three-month-2026' ? '稳定执行 · 每 10 天复盘' : day <= 7 ? '第 1 周 · 适应' : '执行 · 每周复盘',
    dayLabel: `第 ${Math.min(day, end)} / ${end} 天` };
}
